import type { User } from '@/services/user.service';

/** Standard UUID shape — detect stored user ids vs free-text names (e.g. customer sign-off). */
const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isLikelyUserId(value: string): boolean {
  return UUID_LIKE.test(String(value).trim());
}

export function formatUserRecord(u: Pick<User, 'first_name' | 'last_name' | 'username' | 'email'>): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.username || u.email || '';
}

type EmployeeLike = {
  id: string;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  employee_code?: string | null;
  employee_number?: string | null;
};

export function formatEmployeeRecord(e: EmployeeLike): string {
  const name =
    String(e.full_name || '').trim() ||
    [e.first_name, e.last_name].filter(Boolean).join(' ').trim();
  return name || '';
}

export function buildUserIdToLabelMap(users: User[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const u of users) {
    m.set(u.id, formatUserRecord(u));
  }
  return m;
}

export function buildEmployeeIdToLabelMap(employees: EmployeeLike[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const e of employees) {
    m.set(e.id, formatEmployeeRecord(e));
  }
  return m;
}

/**
 * Turn a stored user id (or occasional free-text value) into a display string.
 * - Missing → emptyLabel
 * - Known id in map → full name / username / email
 * - Unknown UUID → "Unknown user" (avoid showing raw ids)
 * - Non-UUID → return as-is (e.g. customer name typed for "received by")
 */
export function resolveUserDisplayFromMap(
  map: Map<string, string>,
  id: string | null | undefined,
  emptyLabel = '—'
): string {
  if (id == null || id === '') return emptyLabel;
  const trimmed = String(id).trim();
  const hit = map.get(trimmed);
  if (hit) return hit;
  if (isLikelyUserId(trimmed)) return 'Unknown user';
  return trimmed;
}

export function resolveEmployeeDisplayFromMap(
  map: Map<string, string>,
  id: string | null | undefined,
  emptyLabel = '—'
): string {
  if (id == null || id === '') return emptyLabel;
  const trimmed = String(id).trim();
  const hit = map.get(trimmed);
  if (hit) return hit;
  if (isLikelyUserId(trimmed)) return 'Unknown employee';
  return trimmed;
}
